import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  DataObject as JsonIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const DataExporter = ({ data = [], columns = [], filename = 'export' }) => {
  const [open, setOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [selectedColumns, setSelectedColumns] = useState(
    columns.map(col => col.field || col.header)
  );
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [includeFilters, setIncludeFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filter data based on date range
  const getFilteredData = () => {
    let filtered = [...data];
    
    if (dateRange.start || dateRange.end) {
      filtered = filtered.filter(row => {
        const dateField = 'date_of_misconduct_event'; // Adjust based on your data structure
        const rowDate = new Date(row[dateField]);
        const start = dateRange.start ? new Date(dateRange.start) : new Date('1900-01-01');
        const end = dateRange.end ? new Date(dateRange.end) : new Date('2100-12-31');
        return rowDate >= start && rowDate <= end;
      });
    }

    return filtered;
  };

  // Get selected column data
  const getExportData = () => {
    const filteredData = getFilteredData();
    return filteredData.map(row => {
      const exportRow = {};
      selectedColumns.forEach(colField => {
        const column = columns.find(col => (col.field || col.header) === colField);
        const header = column ? column.header : colField;
        exportRow[header] = row[colField] || '';
      });
      return exportRow;
    });
  };

  // Export as CSV
  const exportCSV = () => {
    const exportData = getExportData();
    const headers = Object.keys(exportData[0] || {});
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => `"${String(row[header]).replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');

    downloadFile(csvContent, `${filename}.csv`, 'text/csv');
  };

  // Export as Excel
  const exportExcel = () => {
    const exportData = getExportData();
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  // Export as JSON
  const exportJSON = () => {
    const exportData = getExportData();
    const jsonContent = JSON.stringify(exportData, null, 2);
    downloadFile(jsonContent, `${filename}.json`, 'application/json');
  };

  // Export as PDF
  const exportPDF = () => {
    const exportData = getExportData();
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(16);
    doc.text('Data Export Report', 14, 22);
    
    // Add metadata
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 32);
    doc.text(`Records: ${exportData.length}`, 14, 38);
    
    if (exportData.length > 0) {
      const headers = Object.keys(exportData[0]);
      const rows = exportData.map(row => headers.map(header => String(row[header])));
      
      doc.autoTable({
        head: [headers],
        body: rows,
        startY: 45,
        theme: 'striped',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] }
      });
    }
    
    doc.save(`${filename}.pdf`);
  };

  // Download file helper
  const downloadFile = (content, fileName, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Handle export
  const handleExport = async () => {
    setLoading(true);
    setError('');
    
    try {
      switch (exportFormat) {
        case 'csv':
          exportCSV();
          break;
        case 'excel':
          exportExcel();
          break;
        case 'json':
          exportJSON();
          break;
        case 'pdf':
          exportPDF();
          break;
        default:
          throw new Error('Unsupported export format');
      }
      setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle column selection
  const handleColumnToggle = (columnField) => {
    setSelectedColumns(prev => 
      prev.includes(columnField)
        ? prev.filter(col => col !== columnField)
        : [...prev, columnField]
    );
  };

  const exportOptions = [
    { value: 'csv', label: 'CSV', icon: <CsvIcon /> },
    { value: 'excel', label: 'Excel', icon: <CsvIcon /> },
    { value: 'json', label: 'JSON', icon: <JsonIcon /> },
    { value: 'pdf', label: 'PDF', icon: <PdfIcon /> }
  ];

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon />}
        onClick={() => setOpen(true)}
        disabled={!data.length}
      >
        Export Data
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Export Data</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            {/* Export Format */}
            <FormControl fullWidth>
              <InputLabel>Export Format</InputLabel>
              <Select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                label="Export Format"
              >
                {exportOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Column Selection */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Select Columns to Export
              </Typography>
              <FormGroup row>
                {columns.map(column => (
                  <FormControlLabel
                    key={column.field || column.header}
                    control={
                      <Checkbox
                        checked={selectedColumns.includes(column.field || column.header)}
                        onChange={() => handleColumnToggle(column.field || column.header)}
                      />
                    }
                    label={column.header}
                  />
                ))}
              </FormGroup>
            </Box>

            {/* Date Range Filter */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Date Range Filter (Optional)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>

            {/* Export Summary */}
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Export Summary:</strong><br />
                Total Records: {data.length}<br />
                Filtered Records: {getFilteredData().length}<br />
                Selected Columns: {selectedColumns.length}<br />
                Format: {exportFormat.toUpperCase()}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={loading || !selectedColumns.length}
            startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {loading ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DataExporter;
